import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import uuidv4 from 'uuid/v4';
import classnames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { setNodeHover } from 'actions/viewStates';
import { getDistances } from 'constants/matrices';
import OutputToInputWarningModal from './OutputToInputWarningModal';
import NodeAbundanceSlider from './NodeAbundanceSlider';
import { getValueForAbundance } from 'constants/fcm';
import './TrophicNodePlay.scss';
import { useHistory } from 'react-router';
import { useEditableExperiment } from 'hooks/experiments';
import { usePlayModeState } from 'state/PlayModeState';
import { resetMatrix } from 'actions/matrices';

const FixedHandle = ({
  onAbundanceChange,
  node,
  tempOverride,
  height,
  isMoved,
  actualAbundance,
  tempValue,
  setTempValue,
}) => {
  // True when resetting (resets those that user set)
  const isResettingValue = useMemo(() => {
    return !tempOverride;
  }, [tempOverride]);

  // Get the node abundance value
  const nodeAbundance = getValueForAbundance(node, tempValue);

  // Slider bubble CSS
  const bubbleCssFixed = useMemo(() => {
    let bubbleHeight = 20;

    const rangeHeight = document.getElementById(`${node.id}-range`)
      ?.offsetHeight;

    let bottom = `calc(${tempValue * 100}% - ${bubbleHeight / 2}px`;

    if (rangeHeight - bubbleHeight / 2 <= rangeHeight * tempValue) {
      bottom = `${rangeHeight - bubbleHeight}px`;
    }

    if (bubbleHeight / 2 >= rangeHeight * tempValue) {
      bottom = '0px';
    }

    return {
      bottom,
    };
  }, [node, tempValue]);

  const actualValueCssFixed = useMemo(() => {
    let bubbleHeight = 20;
    // Fixed input nodes have originalAbundanceResult = abundance × exaggeration (FCM
    // keeps them pinned at the exaggerated initial value), so we divide back out.
    // Output nodes overridden in play mode have originalAbundanceResult in plain [0,1]
    // scale — dividing would incorrectly deflate their position.
    // Use originalFixed (from the matrix, not the play-mode override) so that
    // output nodes temporarily fixed in play mode don't get the exaggeration
    // divided out of their FCM-equilibrium originalAbundanceResult.
    const originalAbundanceResult = node?.originalFixed
      ? Number(node?.originalAbundanceResult) /
        (node?.abundanceExaggeration ?? 1)
      : Number(node?.originalAbundanceResult);

    const rangeHeight = document.getElementById(`${node.id}-range`)
      ?.offsetHeight;

    let bottom = `calc(${originalAbundanceResult * 100}% - ${bubbleHeight /
      2}px`;

    if (
      rangeHeight - bubbleHeight / 2 <=
      rangeHeight * originalAbundanceResult
    ) {
      bottom = `${rangeHeight - bubbleHeight}px`;
    }

    if (bubbleHeight / 2 >= rangeHeight * originalAbundanceResult) {
      bottom = '0px';
    }

    return {
      bottom,
    };
  }, [node]);

  const onChange = useCallback(
    value => {
      setTempValue(Number(value));
      onAbundanceChange(Number(value));
    },
    [onAbundanceChange, setTempValue]
  );

  useEffect(() => {
    if (isResettingValue) {
      setTempValue(actualAbundance);
    }
  }, [actualAbundance, isResettingValue, setTempValue]);

  // This is only for the tour
  useEffect(() => {
    const handler = e => {
      if (node?.name === 'Hunters') onChange(e.detail.value);
    };

    window.addEventListener('tour:setSliderValue', handler);
    return () => window.removeEventListener('tour:setSliderValue', handler);
  }, [onChange]);

  return (
    <div className="FixedHandle" style={{ height: `${height}px` }}>
      <div id={`${node.id}-range`} className="FixedHandle-range">
        <div className="FixedHandle-actual-value" style={actualValueCssFixed}>
          <div className="stem"></div>
        </div>
        <div
          id={`${node.id}-bubble`}
          className={classnames('FixedHandle-bubble', { isMoved })}
          style={bubbleCssFixed}
        >
          {nodeAbundance}
          <div className="stem"></div>
        </div>
        <input
          className="FixedHandle-slider"
          type="range"
          min="0"
          max="1"
          step="0.01"
          value={tempValue}
          onChange={e => onChange(e.target.value)}
          style={{
            width: `${height}px`,
            left: `${-(height / 2) - 22}px`,
            top: `${height / 2 - 22}px`,
          }}
        />
      </div>
    </div>
  );
};

const TrophicNodePlay = ({
  node,
  matrix,
  nodeOverrides,
  trophicCascadeLevel,
  trophicCascadeLevels,
  addNodeOverride,
  removeNodeOverride,
  fixed,
  trophicLevelInfo,
  nodeScaling,
  tempOverride,
  numNodesAtTrophicLevel,
  canEdit,
}) => {
  const dispatch = useDispatch();
  const [showFixedWarning, setShowFixedWarning] = useState(false);
  const fixedOnMount = useRef(!!fixed);
  const key = useRef(uuidv4().slice(0, 6));
  const history = useHistory();
  const addToNodesAnimated = usePlayModeState(
    state => state.addToNodesAnimated
  );
  const nodesAnimated = usePlayModeState(state => state.nodesAnimated);

  // True when resetting (resets those that user set)
  const isResettingValue = useMemo(() => {
    return !tempOverride;
  }, [tempOverride]);

  useEffect(() => {
    if (isResettingValue && fixed) {
      key.current = uuidv4().slice(0, 6);
    }
  }, [fixed, isResettingValue]);

  const nodeFixedWarningDeclined = useSelector(
    state => state.viewStates.nodeFixedWarningSeen
  );

  useEffect(() => {
    if (!nodeFixedWarningDeclined && !fixedOnMount.current && fixed) {
      setShowFixedWarning(true);
    }
  }, [nodeFixedWarningDeclined, fixed]);

  const hoverNode = useSelector(state => state.viewStates.hoverNode);
  const hoverNodeConnected =
    hoverNode &&
    [
      ...Object.keys(matrix.nodes[hoverNode]?.influences || {}),
      ...Object.values(matrix?.nodes || {}).reduce(
        (acc, n) =>
          Object.keys(n?.influences || {}).indexOf(hoverNode) >= 0
            ? [...acc, n.id]
            : acc,
        []
      ),
    ].indexOf(node.id) >= 0;

  // Gets the distance between two nodes
  const getDistance = (originNodeId, destinationNodeId) =>
    getDistances(matrix, originNodeId)[destinationNodeId];

  const nodeDistance = Math.min(
    ...nodeOverrides.map(d => getDistance(d.id, node.id))
  );

  let currentDistance = trophicCascadeLevels.indexOf(trophicCascadeLevel) + 1;
  currentDistance =
    currentDistance === trophicCascadeLevels.length
      ? Infinity
      : currentDistance;

  const cascadeHidden = nodeOverrides.length && nodeDistance > currentDistance;

  // Uses a local state for the abundance value that shows when dragging
  const [abundance, setAbundance] = useState(Number(node.abundanceResult));

  // Determine if the abundance increased or decreased
  const cascadeChange =
    nodeOverrides.length && node.oldAbundance !== abundance
      ? node.oldAbundance < abundance
        ? 'increase'
        : 'decrease'
      : '';

  // Only shows cascadeChange when it is the current distance
  const nodeCascadeChange =
    nodeOverrides.length &&
    currentDistance === nodeDistance &&
    currentDistance !== Infinity
      ? cascadeChange
      : '';

  // Make sure the abundances are changed when the node is updated from parent
  useEffect(() => setAbundance(node.abundanceResult), [node]);

  // Called when abundance changes via slider
  const onAbundanceChange = value => {
    const newAbundance = parseFloat(value);
    setAbundance(newAbundance);
    addNodeOverride({ ...node, abundance: newAbundance, fixed: true });
  };

  const onNodeHover = nodeId => e => {
    e.stopPropagation();
    if (hoverNode !== nodeId) dispatch(setNodeHover(nodeId));
  };

  const onCancelWarningModal = useCallback(() => {
    removeNodeOverride(node);
    setShowFixedWarning(false);
  }, [removeNodeOverride, node]);

  const onNodeClick = useCallback(
    e => {
      // Ignore if clicking the handle
      if (e.target.className.includes('NodeAbundanceSlider')) {
        return;
      }

      history.push(`/matrices/${matrix?.id}/overrides/${node.id}`);
    },
    [history, node, matrix]
  );

  useEffect(() => {
    // Maybe need to track before/after? What happens if I change nodes? Maybe doesn't matter since I must reset to edit?
    if (nodeCascadeChange && !nodesAnimated?.includes(node.id)) {
      addToNodesAnimated(node.id);
    }
  }, [addToNodesAnimated, node, nodeCascadeChange, nodesAnimated]);

  // This is more stable than abundance because of cascading logic
  const actualAbundance = useMemo(() => {
    return node.fixed ? Number(node.abundance) : Number(node.abundanceResult);
  }, [node]);

  // Active value of changing the setting range
  const [tempValue, setTempValue] = useState(actualAbundance);

  // actualAbundance needs a second to set
  useEffect(() => {
    if (actualAbundance) {
      setTempValue(actualAbundance);
    }
  }, [actualAbundance]);

  const isMoved = useMemo(() => {
    const baseline = node.fixed
      ? Number(node.abundance)
      : Number(node?.originalAbundanceResult);
    return tempValue.toFixed(3) !== baseline.toFixed(3);
  }, [tempValue, node]);

  return (
    <>
      <div
        className={classnames('TrophicNodePlay', {
          fixed: fixed && numNodesAtTrophicLevel > 1,
          'interactiveTourGettingStarted-6': fixed && node?.name === 'Hunters',
        })}
      >
        <div className="TrophicNodePlay-outer">
          {fixed ? (
            <FixedHandle
              onAbundanceChange={onAbundanceChange}
              node={node}
              tempOverride={tempOverride}
              height={nodeScaling * 100}
              isMoved={isMoved}
              actualAbundance={actualAbundance}
              tempValue={tempValue}
              setTempValue={setTempValue}
            />
          ) : null}
          <div
            key={key.current}
            style={{
              width: `${nodeScaling * 100}px`,
              height: `${nodeScaling * 100}px`,
            }}
            className={classnames(
              'TrophicNodePlay-container',
              `id${node.id}`, //id tag is for the tour
              nodeCascadeChange,
              nodeCascadeChange,
              hoverNode && (hoverNode === node.id ? 'hover' : 'fadedOut'),
              hoverNodeConnected && 'hoverConnected',
              {
                'TrophicNodePlay-fixed-border': fixed,
                isMoved,
                'tour-node-click': node?.name === 'Hunters',
              }
            )}
            onMouseMove={onNodeHover(node.id)}
            onClick={onNodeClick}
          >
            <div className={classnames('TrophicNodePlayImageWrap', { fixed })}>
              {node.imageUrl ? (
                <img
                  className="TrophicNodePlayImage"
                  src={node.imageUrl}
                  alt={node.name}
                />
              ) : null}
            </div>

            <div
              className={classnames('TrophicNodePlayInner', { fixed })}
              key={`${node?.id}-${node?.originalAbundanceResult}`}
            >
              {!fixed ? (
                <NodeAbundanceSlider
                  node={node}
                  abundance={abundance}
                  cascadeHidden={cascadeHidden}
                  onAbundanceChange={onAbundanceChange}
                  fixed={fixed}
                  tempOverride={tempOverride}
                  noAnimate={trophicCascadeLevel === 'Equilibrium'}
                />
              ) : null}
              <div className="TrophicNodePlayInner-name-container">
                <div
                  className={classnames('TrophicNodePlayInner-name', {
                    positive: node?.oldAbundance < abundance,
                    negative: node?.oldAbundance > abundance,
                  })}
                >
                  {node.name}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showFixedWarning ? (
        <OutputToInputWarningModal
          onExit={() => setShowFixedWarning(false)}
          onCancel={onCancelWarningModal}
        />
      ) : null}
    </>
  );
};

export default TrophicNodePlay;
